// Pure helpers for the worker-search-trigger flow (the "look up this company" / "request a check" /
// "general scan" actions), ported from catalyst_tracker/static/tracker.js:1496-1511. The real,
// currently-functional backend path is POST /catalyst-tracker/api/tool/fire_trigger — see
// catalystTrackerApi.fireTrigger() and catalyst_tracker/worker/jobs.py's enqueue_search()/
// search_id_of(). tracker.js's own fireSearch() additionally writes straight to a Firestore
// `searches` collection via a `db` handle that is never initialized in the live Flask deployment (no
// Firebase config) — that half of fireSearch is dead code there; only its `mcp.callTool("fire_trigger",
// {text: "SEARCH_ID: ...\nKEYWORD: ..."})` call corresponds to a real, working path, and that's the one
// ported here (as a direct REST call, no MCP indirection needed client-side).

export const cleanTerm = (q: string): string =>
  q.replace(/[^A-Za-z0-9 .,+\-/()']/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);

export const slugOf = (t: string): string =>
  t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

/** slug for a per-catalyst "does this affect company C?" check request (tracker.js:648). */
export const preqSlug = (company: string, catalystId: string): string => slugOf(`link ${company} ${catalystId}`);

/** The worker runs on a 6-hour cadence, :17 past the hour at 00/06/12/18 UTC (tracker.js:1499). */
export function nextCheck(now: number = Date.now()): Date {
  const d = new Date(now);
  d.setUTCMinutes(17, 0, 0);
  d.setUTCHours(Math.floor(d.getUTCHours() / 6) * 6);
  if (d.getTime() <= now) d.setTime(d.getTime() + 6 * 36e5);
  return d;
}

export function nextCheckLabel(now: number = Date.now()): string {
  try {
    return nextCheck(now)
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
      .replace(/[  ]/g, ' ');
  } catch {
    return '';
  }
}

export function queuedMsg(what = 'Queued', now: number = Date.now()): string {
  const t = nextCheckLabel(now);
  return `${what} · runs at the next check${t ? ` (about ${t})` : ', within about 6 hours'}`;
}

export function fmtTime(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** At most DEEP_MAX "deeper searches" per person per rolling hour (tracker.js:1507-1511), tracked via
 * the already-existing `watch.searchLog` field. */
export const DEEP_MAX = 3;

export const deepUsed = (searchLog: string[] | undefined): string[] =>
  (searchLog || []).filter((t) => Date.now() - Date.parse(t) < 3600e3).sort();

export const deepLeft = (searchLog: string[] | undefined): number =>
  Math.max(0, DEEP_MAX - deepUsed(searchLog).length);

export const deepNext = (searchLog: string[] | undefined): Date | null => {
  const u = deepUsed(searchLog);
  return u.length ? new Date(Date.parse(u[0]) + 3600e3) : null;
};

export const hhmm = (d: Date | null): string => (d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');

/** The statuses `enqueue_search()` (or an older Firestore-era writer) can leave a search doc in while
 * it's still outstanding. The Python backend only ever sets "started" directly — "queued"/"throttled"
 * are kept for message-copy parity with tracker.js, which still checks for them. */
export const ACTIVE_SEARCH_STATUSES = ['queued', 'started', 'running'] as const;
